import axios from "axios";
import React, { useState, useEffect } from "react";
import { path } from "../../path";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

function fmt(amount) {
  return "₹" + Number(amount).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function PlatformWalletDetails() {
  const [balance, setBalance] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const { token } = useSelector((state) => state.AuthReducer);

  const config = {
    headers: {
      "Access-Control-Allow-Origin": `${path}`,
      Authorization: `Bearer ${token}`,
    },
  };

  useEffect(() => {
    const fetchBalance = async () => {
      try {
        const response = await axios.get(
          `${path}/marketing/platformWallet`,
          config
        );
        setBalance(response.data.wallet?.balance);
      } catch (error) {
        toast.error("Failed to fetch wallet balance.");
        console.error("Error fetching wallet balance:", JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    };
    fetchBalance();
  }, []);

  const handleUpdate = async () => {
    const parsed = parseFloat(inputValue);
    if (isNaN(parsed) || parsed < 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${path}/marketing/updatePlatformWallet`,
        { amount: parsed },
        config
      );

      if (response.status === 200) {
        setBalance(response.data.wallet.balance);
        setInputValue("");
        toast.success("Wallet balance updated successfully!");
      } else {
        toast.error(response.data.error || "Something went wrong!");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Something went wrong!");
      console.error("Error updating wallet balance:", JSON.stringify(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 pt-2 pb-8 px-4">

      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="flex items-baseline justify-between mb-5">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">
            Platform Wallet
          </h1>
        </div>

        {/* Card */}
        <div className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm">

          {/* Balance Display */}
          <div className="p-5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1">
              Current Balance
            </p>
            {loading ? (
              <p className="text-2xl font-mono font-semibold text-gray-300 animate-pulse">
                ₹ —
              </p>
            ) : (
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-mono font-semibold text-gray-900">
                  {balance !== null ? fmt(balance) : "—"}
                </p>
                <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">
                  live
                </span>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Update Section */}
          <div className="p-5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-3">
              Add Balance
            </p>

            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-sm pointer-events-none">
                  ₹
                </span>
                <input
                  type="number"
                  min="0"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
                  placeholder="Enter amount"
                  className="w-full pl-7 pr-3 py-2 text-sm font-mono border border-gray-200 rounded-lg
                    focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-transparent
                    placeholder:text-gray-300 bg-gray-50"
                />
              </div>

              <button
                onClick={handleUpdate}
                disabled={saving || !inputValue}
                className="flex items-center justify-center gap-1.5
                  text-sm font-medium text-emerald-700
                  bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2
                  hover:bg-emerald-100 active:scale-[0.97]
                  transition-all duration-100 cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed">
                <span className="text-xs font-bold">✓</span>
                {saving ? "Adding..." : "Add"}
              </button>
            </div>

            <p className="text-[11px] text-gray-400 mt-2">
              This will add the amount in the current wallet balance.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}